// ─────────────────────────────────────────────────────────────────────────────
// OUTFORM — CONCEPT 5: ACTIVITY FEED
//
// Atlas draft, 25 Aug 2026. Sibling to concept3 (Review Queue) and concept4
// (Command Panel) — all three exploring a different docked-panel interaction
// model for the same underlying job, none of them an ordinary chat thread.
//
// The idea this one exists to show: the docked panel is AMBIENT by default,
// not something the estimator drives turn by turn. It is a live, narrated log
// of what the agent is doing right now — short present-tense lines that
// settle from "in progress" to a checkmark as each finishes, accumulating
// top-to-bottom, auto-scrolling to the newest line. The estimator mostly
// WATCHES. Silence is a normal, comfortable state here — nothing is waiting
// on them. They can interject, but an interjection is a short, occasional,
// directive line INSERTED into the same log (never a chat bubble, never a
// growing message-and-reply thread), and the log resumes narrating in direct
// response to it. Routine progress reads plain; a genuine judgment call or a
// flag reads amber, so the estimator learns to scan for the one thing that
// actually needs their eyes.
//
// ── WHAT THIS IS, PLAINLY ────────────────────────────────────────────────
// A click-through visual demo, not a narrated video — no audio. Play advances
// on a fixed timer; Prev/Next/dots are the scrub controls; the caption under
// the controls is plain text.
//
// ── BOUNDARIES ───────────────────────────────────────────────────────────
// This file imports NOTHING from board/outform/ (Quinn's live Outform board)
// and nothing outside this file imports FROM here. The mock sheet is a styled
// grid, not the real Univer library — a design comparison, not a build. Own
// CSS, own prefix (`.oc5-`), so nothing can collide with the sibling concepts
// or with Quinn's `.of-*` classes, even though the visual language (clean
// grid, docked panel, cell-level provenance) is deliberately the same family.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from "react";
import { animate, stagger } from "animejs";
import "./outform-concept5.css";

const money = (n: number) => "$" + Math.round(n).toLocaleString("en-US");

// ── the job — same underlying scenario as the sibling concepts ─────────────
const UNITS = 340;
const ASSEMBLY_RATE_ORIG = 46.5;
const ASSEMBLY_RATE_FIXED = 37.8;
const PRICE_ORIGINAL = 158900;
const PRICE_PUSHBACK = 145000;
const MARGIN_FLOOR = 0.55;

type ItemRow = { kind: "item"; id: string; label: string; qty: number; unit: string; rate: number };
type RowDef = { kind: "colhead" } | { kind: "section"; label: string } | ItemRow;

const LABOR_IDS = ["assembly", "fabrication", "finishing", "qc", "install"];

const SHEET_ROWS: RowDef[] = [
  { kind: "colhead" },
  { kind: "section", label: "Labor" },
  { kind: "item", id: "assembly", label: "Assembly labor", qty: UNITS, unit: "hrs", rate: ASSEMBLY_RATE_ORIG },
  { kind: "item", id: "fabrication", label: "Fabrication labor", qty: UNITS, unit: "hrs", rate: 34.0 },
  { kind: "item", id: "finishing", label: "Finishing labor", qty: UNITS, unit: "hrs", rate: 19.25 },
  { kind: "item", id: "qc", label: "QC & inspection", qty: UNITS, unit: "hrs", rate: 8.4 },
  { kind: "item", id: "install", label: "Install labor", qty: UNITS, unit: "hrs", rate: 6.1 },
  { kind: "section", label: "Materials" },
  { kind: "item", id: "base", label: "ABS moulded base", qty: UNITS, unit: "ea", rate: 28.4 },
  { kind: "item", id: "acrylic", label: "Acrylic panel, edge-lit", qty: UNITS, unit: "ea", rate: 41.9 },
  { kind: "item", id: "led", label: "LED module + driver", qty: UNITS, unit: "ea", rate: 22.15 },
  { kind: "item", id: "hardware", label: "Hardware & fasteners", qty: UNITS, unit: "ea", rate: 3.6 },
];

function totalCost(assemblyFixed: boolean): number {
  return SHEET_ROWS.reduce((sum, r) => {
    if (r.kind !== "item") return sum;
    const rate = r.id === "assembly" && assemblyFixed ? ASSEMBLY_RATE_FIXED : r.rate;
    return sum + r.qty * rate;
  }, 0);
}

// the three comparable jobs the feed cites while checking the assembly rate
const COMPS = [
  { job: "Sephora · Rouge endcap", rate: 37.8 },
  { job: "Ulta · Fragrance bay", rate: 38.1 },
  { job: "Nike · Kids footwear wall", rate: 37.4 },
];
const COMP_AVG = COMPS.reduce((s, c) => s + c.rate, 0) / COMPS.length;

type Step = { n: number; title: string; note: string };
const STEPS: Step[] = [
  { n: 1, title: "An empty job", note: "Plain spreadsheet. The feed is idle, waiting." },
  { n: 2, title: "A brief lands", note: "Drop the RFQ; the feed starts narrating immediately." },
  { n: 3, title: "It keeps talking", note: "Short lines land one after another as it reads." },
  { n: 4, title: "The sheet fills in", note: "Labor rows land first while materials keep pricing." },
  { n: 5, title: "A judgment call", note: "Not routine progress — a flagged finding, marked differently." },
  { n: 6, title: "Ballpark complete", note: "First pass lands; the log marks the milestone." },
  { n: 7, title: "Just watching", note: "No new lines. Quiet is a normal state here." },
  { n: 8, title: "A short redirect", note: "One line, inserted into the log, not a question." },
  { n: 9, title: "It reacts", note: "Narration resumes in direct response to the redirect." },
  { n: 10, title: "Proof, on the cell", note: "A small provenance mark, sourced from the feed." },
  { n: 11, title: "A bigger ask", note: "Another short redirect — this one touches everything." },
  { n: 12, title: "Recalculating", note: "Every line moves; a new finding gets flagged." },
  { n: 13, title: "Decide, in place", note: "Two buttons live under the flagged line itself." },
  { n: 14, title: "The point", note: "You don't have to ask. It already told you." },
];

// ── the feed log ─────────────────────────────────────────────────────────
type Ctx = { compAvg: number; asmOrig: number; asmFixed: number; margin: number; price: number; cost: number };
type FeedKind = "log" | "finding" | "user" | "divider";
type FeedEntry = {
  id: string;
  at: number;
  kind: FeedKind;
  t?: string;
  progressable?: boolean;
  text?: string;
  render?: (ctx: Ctx) => string;
  headline?: (ctx: Ctx) => string;
};

const FEED: FeedEntry[] = [
  { id: "read", at: 2, kind: "log", t: "0:02", progressable: true, text: "Reading Nordstrom_Denim_Endcap_RFQ_v2.pdf…" },
  { id: "dims", at: 3, kind: "log", t: "0:05", text: "Read 2 sheets, 9 dimensions, 4 materials called out" },
  { id: "type", at: 3, kind: "log", t: "0:07", text: "Identified the display type — endcap bay, edge-lit acrylic panel" },
  { id: "comps", at: 3, kind: "log", t: "0:11", text: "Searching decided jobs… 612 scanned, 14 close matches" },
  { id: "labor", at: 4, kind: "log", t: "0:14", text: "Costed the labor routes — 5 lines" },
  { id: "bom", at: 4, kind: "log", t: "0:17", progressable: true, text: "Pricing the bill of materials…" },
  { id: "bom-done", at: 5, kind: "log", t: "0:20", text: "Priced the bill of materials — 4 lines" },
  {
    id: "finding-band",
    at: 5,
    kind: "finding",
    text: "This client has 2 prior jobs priced below the standard band — flagging for review.",
  },
  { id: "margin-band", at: 6, kind: "log", t: "0:24", text: `Applied the margin band — ${Math.round(MARGIN_FLOOR * 100)}% of cost` },
  { id: "ballpark", at: 6, kind: "divider", headline: () => `Ballpark complete · ${money(PRICE_ORIGINAL)}` },
  { id: "user-assembly", at: 8, kind: "user", text: "check the assembly rate against comps" },
  { id: "check1", at: 9, kind: "log", t: "0:31", text: "Checking assembly labor against 3 comparable jobs…" },
  {
    id: "check2",
    at: 9,
    kind: "log",
    t: "0:33",
    render: (c) => `Comp average is $${c.compAvg.toFixed(2)}/hr, this line is $${c.asmOrig.toFixed(2)}/hr`,
  },
  { id: "check3", at: 9, kind: "log", t: "0:34", progressable: true, text: "Updating the cell…" },
  {
    id: "fixed",
    at: 10,
    kind: "log",
    t: "0:36",
    render: (c) => `Rate corrected to $${c.asmFixed.toFixed(2)}/hr — comp average attached to the cell`,
  },
  { id: "user-price", at: 11, kind: "user", text: `client wants it near ${money(PRICE_PUSHBACK)}, recalc everything` },
  { id: "recalc1", at: 12, kind: "log", t: "1:02", text: "Re-running margin across every line…" },
  { id: "recalc2", at: 12, kind: "log", t: "1:04", text: "The assembly fix carries through" },
  {
    id: "margin-flag",
    at: 12,
    kind: "finding",
    render: (c) =>
      `New margin is ${(c.margin * 100).toFixed(1)}% — under your ${Math.round(MARGIN_FLOOR * 100)}% floor. Flagging rather than sending automatically.`,
  },
];

export function OutformConceptFive({ width = 1710 }: { width?: number }) {
  const APP_W = 1680;
  const APP_H = 1000;
  const scale = width / APP_W;

  const [step, setStep] = useState(1);
  const [playing, setPlaying] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);
  const feedBody = useRef<HTMLDivElement | null>(null);

  const s = STEPS[step - 1];

  const advance = useCallback(() => {
    setStep((cur) => {
      if (cur >= STEPS.length) {
        setPlaying(false);
        return cur;
      }
      return cur + 1;
    });
  }, []);

  const go = useCallback((n: number) => setStep(Math.max(1, Math.min(STEPS.length, n))), []);

  // ── PLAY — a fixed timer, not audio. Click-through, not a narrated video.
  const STEP_MS = 3400;
  useEffect(() => {
    if (!playing) return;
    const t = window.setInterval(() => advance(), STEP_MS);
    return () => window.clearInterval(t);
  }, [playing, advance]);

  // reveal-in animation + feed auto-scroll, same technique as the sibling
  // concepts: any element tagged `.oc5-at-N` fades/slides in on arrival at
  // step N; newly-settled log dots additionally fill from grey to green.
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const q = (sel: string) => Array.from(el.querySelectorAll(sel)) as HTMLElement[];
    const m = q(`.oc5-at-${step}`);
    if (m.length) animate(m, { opacity: [0, 1], y: [8, 0], duration: 380, delay: stagger(80) });
    const dots = q(`.oc5-at-${step} .oc5-dot.is-done`);
    if (dots.length)
      animate(dots, { scale: [0, 1], backgroundColor: ["#e2e6ec", "#0f7a4d"], duration: 320, delay: stagger(180) });
    const body = feedBody.current;
    if (body) body.scrollTo({ top: body.scrollHeight, behavior: "smooth" });
  }, [step]);

  // ── derived state, one flag per beat of the arc ─────────────────────────
  const sheetPhase: "empty" | "reading" | "thinking" | "partial" | "full" =
    step === 1 ? "empty" : step === 2 ? "reading" : step === 3 ? "thinking" : step === 4 ? "partial" : "full";
  const commentApplied = step >= 10; // assembly rate corrected on the sheet
  const broadPushback = step >= 11; // client's target price is live
  const assemblyState: "idle" | "selected" | "checking" | "fixed" =
    step === 8 ? "selected" : step === 9 ? "checking" : step >= 10 ? "fixed" : "idle";
  const closing = step === 14;

  const cost = totalCost(commentApplied);
  const price = broadPushback ? PRICE_PUSHBACK : PRICE_ORIGINAL;
  const margin = (price - cost) / price;
  const ctx: Ctx = { compAvg: COMP_AVG, asmOrig: ASSEMBLY_RATE_ORIG, asmFixed: ASSEMBLY_RATE_FIXED, margin, price, cost };

  const visible = FEED.filter((e) => e.at <= step);
  let lastLogIdx = -1;
  visible.forEach((e, i) => {
    if (e.kind === "log") lastLogIdx = i;
  });

  const rowsToShow =
    sheetPhase === "partial"
      ? SHEET_ROWS.filter((r) => !(r.kind === "item" && !LABOR_IDS.includes(r.id)) && !(r.kind === "section" && r.label === "Materials"))
      : SHEET_ROWS;

  return (
    <div style={{ width }}>
      <div className="nowheel nodrag nopan" style={{ width, height: APP_H * scale, overflow: "hidden", borderRadius: 14 }}>
        <div
          ref={root}
          className="oc5-root"
          style={{ width: APP_W, height: APP_H, transform: `scale(${scale})`, transformOrigin: "top left" }}
        >
          <div className="oc5-flag">
            Design prototype · illustrative numbers · draft concept for comparison, not the shipped UI · the
            spreadsheet below is a mock, not the real Univer sheet
          </div>

          {closing ? (
            <Closing />
          ) : (
            <div className="oc5-app" dir="ltr">
              <section className="oc5-sheetpane">
                <SheetHead phase={sheetPhase} step={step} />
                <div className="oc5-sheetbody">
                  {sheetPhase === "empty" ? (
                    <EmptySheet />
                  ) : sheetPhase === "reading" ? (
                    <ReadingDrop />
                  ) : sheetPhase === "thinking" ? (
                    <ThinkingSheet />
                  ) : (
                    <div className="oc5-grid">
                      {rowsToShow.map((def, i) => (
                        <Row key={i} def={def} assemblyState={assemblyState} />
                      ))}
                      {sheetPhase === "partial" ? (
                        <div className="oc5-skel">
                          <span className="oc5-skel-pulse" /> Materials, margin and total — still pricing…
                        </div>
                      ) : (
                        <div className="oc5-summary">
                          <div className={`oc5-summary-row ${broadPushback ? "is-flash" : ""} ${margin < MARGIN_FLOOR ? "is-warn" : ""}`}>
                            <span>Margin</span>
                            <b>{(margin * 100).toFixed(1)}%</b>
                            <em>{margin < MARGIN_FLOOR ? `under the ${Math.round(MARGIN_FLOOR * 100)}% floor` : "on target"}</em>
                          </div>
                          <div className={`oc5-summary-row ${broadPushback ? "is-flash" : ""}`}>
                            <span>Total price</span>
                            <b>{money(price)}</b>
                            <em>cost {money(cost)}</em>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </section>

              <section className="oc5-feed">
                <div className="oc5-feed-head">
                  <span className="oc5-feed-avatar" />
                  <div>
                    <b>Activity</b>
                    <span>Narrating automatically · redirect only if you need to</span>
                  </div>
                </div>
                <div className="oc5-feed-body" ref={feedBody}>
                  {visible.length === 0 ? (
                    <div className="oc5-feed-idle">
                      <span className="oc5-feed-idle-dot" />
                      <p>Waiting for a brief.</p>
                    </div>
                  ) : (
                    visible.map((e, i) => (
                      <FeedRow
                        key={e.id}
                        entry={e}
                        ctx={ctx}
                        doing={e.kind === "log" && i === lastLogIdx && !!e.progressable}
                        step={step}
                      />
                    ))
                  )}
                </div>
                <div className={`oc5-interject ${step === 8 || step === 11 ? "is-active" : ""}`}>
                  <span className="oc5-interject-arrow">→</span>
                  <input readOnly placeholder="Redirect it, if you need to…" />
                </div>
              </section>
            </div>
          )}
        </div>
      </div>

      <div className="oc5-ctl nowheel nodrag nopan">
        <button onClick={() => go(step - 1)} disabled={step === 1}>
          ‹
        </button>
        <div className="oc5-ctl-dots">
          {STEPS.map((x) => (
            <button key={x.n} className={x.n === step ? "on" : ""} onClick={() => go(x.n)} title={x.title} />
          ))}
        </div>
        <button onClick={() => go(step + 1)} disabled={step === STEPS.length}>
          ›
        </button>
        <button className="oc5-play" onClick={() => setPlaying((p) => !p)}>
          {playing ? "Pause" : "Play"}
        </button>
        <div className="oc5-ctl-text">
          <b>
            {step} / {STEPS.length} — {s.title}
          </b>
          <span>{s.note}</span>
        </div>
      </div>
    </div>
  );
}

// ── the empty / landing / thinking states ───────────────────────────────
function EmptySheet() {
  return (
    <div className="oc5-empty">
      <div className="oc5-empty-icon">+</div>
      <h3>No job open yet</h3>
      <p>Drop a brief onto the sheet to begin. The feed will do the rest.</p>
    </div>
  );
}

function ReadingDrop() {
  return (
    <div className="oc5-drop oc5-at-2">
      <div className="oc5-drop-chip">
        <span className="oc5-file-ic">PDF</span>
        Nordstrom_Denim_Endcap_RFQ_v2.pdf
      </div>
      <p>Landed on the sheet — reading now.</p>
    </div>
  );
}

function ThinkingSheet() {
  return (
    <div className="oc5-thinking">
      <div className="oc5-thinking-in">
        <span className="oc5-thinking-pulse" />
        <p>Building the estimate…</p>
      </div>
    </div>
  );
}

function SheetHead({ phase, step }: { phase: "empty" | "reading" | "thinking" | "partial" | "full"; step: number }) {
  const named = phase !== "empty" && phase !== "reading";
  return (
    <div className="oc5-sheethead">
      <div className="oc5-sheethead-title">
        <b>{named ? "Nordstrom · Denim Bay End-cap" : "New estimate"}</b>
        <em>{named ? "NB-2201_Estimate · 340 units" : "—"}</em>
      </div>
      {phase !== "empty" ? (
        <span className="oc5-file-tag">
          <span className="oc5-file-ic">PDF</span>Nordstrom_Denim_Endcap_RFQ_v2.pdf
        </span>
      ) : null}
      {phase === "full" && step >= 6 ? <span className="oc5-ready">Ballpark ready</span> : null}
    </div>
  );
}

function Row({ def, assemblyState }: { def: RowDef; assemblyState: "idle" | "selected" | "checking" | "fixed" }) {
  if (def.kind === "colhead") {
    return (
      <div className="oc5-row oc5-row-head">
        <span className="oc5-c oc5-c-label">Item</span>
        <span className="oc5-c oc5-c-qty">Qty</span>
        <span className="oc5-c oc5-c-unit">Unit</span>
        <span className="oc5-c oc5-c-rate">Rate</span>
        <span className="oc5-c oc5-c-ext">Ext. cost</span>
      </div>
    );
  }
  if (def.kind === "section") {
    return (
      <div className="oc5-row oc5-row-section">
        <span className="oc5-c oc5-c-label">{def.label}</span>
      </div>
    );
  }

  const isAssembly = def.id === "assembly";
  const rate = isAssembly && assemblyState === "fixed" ? ASSEMBLY_RATE_FIXED : def.rate;
  const ext = def.qty * rate;

  const cls = [
    "oc5-row",
    isAssembly && assemblyState === "selected" && "is-selected",
    isAssembly && assemblyState === "checking" && "is-checking",
    isAssembly && assemblyState === "fixed" && "is-fixed",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cls}>
      <span className="oc5-c oc5-c-label">
        {def.label}
        {isAssembly && assemblyState === "fixed" ? (
          <span
            className="oc5-comment-badge"
            title={`Rate corrected from $${ASSEMBLY_RATE_ORIG.toFixed(2)}/hr to $${ASSEMBLY_RATE_FIXED.toFixed(
              2,
            )}/hr — comp average across 3 similar jobs was $${COMP_AVG.toFixed(2)}/hr. Sourced from the activity feed, not a comment thread.`}
          />
        ) : null}
      </span>
      <span className="oc5-c oc5-c-qty">{def.qty}</span>
      <span className="oc5-c oc5-c-unit">{def.unit}</span>
      <span className="oc5-c oc5-c-rate">${rate.toFixed(2)}</span>
      <span className="oc5-c oc5-c-ext">{money(ext)}</span>
    </div>
  );
}

function FeedRow({ entry, ctx, doing, step }: { entry: FeedEntry; ctx: Ctx; doing: boolean; step: number }) {
  const text = entry.render ? entry.render(ctx) : entry.text ?? "";
  const cls = `oc5-feed-row oc5-kind-${entry.kind} oc5-at-${entry.at}`;

  if (entry.kind === "divider") {
    return (
      <div className={cls}>
        <div className="oc5-divider">
          <span>{entry.headline ? entry.headline(ctx) : ""}</span>
        </div>
      </div>
    );
  }

  if (entry.kind === "user") {
    return (
      <div className={cls}>
        <span className="oc5-you-tag">→ You</span>
        <p>{text}</p>
      </div>
    );
  }

  if (entry.kind === "finding") {
    return (
      <div className={cls}>
        <span className="oc5-dot oc5-dot-finding" />
        <div className="oc5-feed-txt">
          <p>{text}</p>
          {entry.id === "margin-flag" && step >= 13 ? (
            <div className="oc5-feed-decide oc5-at-13">
              <button className="oc5-btn-primary">Send anyway</button>
              <button className="oc5-btn-secondary">Hold it</button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  // log
  return (
    <div className={cls}>
      <span className={`oc5-dot ${doing ? "is-doing" : "is-done"}`} />
      <div className="oc5-feed-txt">
        <p>{text}</p>
        {entry.t ? <em>{entry.t}</em> : null}
      </div>
    </div>
  );
}

function Closing() {
  return (
    <div className="oc5-closing oc5-at-14">
      <div className="oc5-closing-card">
        <div className="oc5-closing-kicker">Outform — Concept 5</div>
        <h2>You don't have to ask what it's doing — it already told you.</h2>
        <p>You only have to talk when you want something to change.</p>
        <div className="oc5-closing-split">
          <div>
            <b>The feed</b>
            <span>A running narration of the work, settling into a record as it goes. Silence is fine.</span>
          </div>
          <div>
            <b>The redirect</b>
            <span>Two short lines, inserted only when something needs to change. Never a chat thread.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OutformConceptFive;
